import { push } from '@lagunovsky/redux-react-router'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import HomeAction from 'home/actions/HomeAction'
import { find, omit, orderBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { TELECOM_ID } from 'materiel/constants/MaterielConstants'
import { H_MAT_TELECOM } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { getDate } from '../../../../utils/DateUtil'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getUser } from '../../../../utils/SettingUtils'
import { isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import PowerSupplyAction from '../../powerSupply/actions/PowerSupplyAction'
import SimAction from '../../sim/actions/SimAction'
import SimSubscriptionTable from '../../subscription/components/SimSubscriptionTable'
import TelecomAction from '../actions/TelecomAction'
import TelecomPanel from './TelecomPanel'

const TelecomApp = () => {
    const {
        telecom: storeTelecom,
        telecomTypes,
        telecomSituations,
        telecomPictures,
        telecomFiles,
        telecoms,
        telecomsLastSituations,
        powerSupplyTypes,
        materielSettingRules,
        simsLink,
        sims,
        simTypes,
    } = useSelector(store => ({
        telecom: store.TelecomReducer.telecom,
        telecomTypes: store.TelecomReducer.telecomTypes,
        telecomSituations: store.TelecomReducer.telecomSituations,
        telecomPictures: store.TelecomReducer.telecomPictures,
        telecomFiles: store.TelecomReducer.telecomFiles,
        telecoms: store.TelecomReducer.telecoms,
        telecomsLastSituations: store.TelecomReducer.telecomsLastSituations,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
        simsLink: store.TelecomReducer.simsLink,
        sims: store.SimReducer.sims,
        simTypes: store.SimReducer.simTypes,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const [telecom, setTelecom] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const fetchTelecom = id => dispatch(TelecomAction.fetchTelecom(id)).then(json => {
        if (json.id) {
            dispatch(TelecomAction.fetchTelecomSituations(json.id))
            dispatch(TelecomAction.fetchTelecomPictures(json.id))
            dispatch(TelecomAction.fetchTelecomFiles(json.imei))
            dispatch(TelecomAction.fetchSimsLink(json.id))
            setTelecom({ ...json })
        }
    })

    const changeAdministrator = administrator => setTelecom(prev => ({ ...prev, administrator }))

    const changeTelecomValue = changes => setTelecom(prev => ({ ...prev, ...changes }))

    const deleteSituation = id => dispatch(TelecomAction.deleteTelecomSituation(id, params.id))

    const filterConsultant = actions => getUser().consultant === '1'
        ? omit(actions, ['save', 'edit', 'delete', 'replace'])
        : actions

    useActions(() => {
        updateMaterialize()
        if (params.id === 'new' && !storeTelecom?.id) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === TELECOM_ID)
                    if (isSaveBlocked(telecoms, telecom, rules, 'telecomType')) return

                    dispatch(TelecomAction.createTelecom(telecom)).then(id => {
                        if (id) {
                            const situation = {
                                idTelecom: id,
                                situationDate: telecom.purchaseDate,
                                statusCode: 2,
                            }
                            dispatch(TelecomAction.saveTelecomSituation(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push('/materiel/telecom'))
                },
            })
        }
        if (isEditMode) {
            return filterConsultant({
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === TELECOM_ID)
                    if (isSaveBlocked(telecoms, telecom, rules, 'telecomType')) return

                    const type = telecomTypes.find(({ id }) => id === telecom.telecomType)
                    const currentLink = simsLink.find(({ endDate }) => !endDate)
                    if (currentLink && ![1, 2, 3, 4].includes(type?.code)) {
                        dispatch(TelecomAction.closeLastTelecomSim(currentLink.idSim)).then(() => {
                            dispatch(TelecomAction.fetchSimsLink(telecom.id))
                        })
                    }
                    dispatch(TelecomAction.updateTelecom(telecom)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setTelecom(storeTelecom)
                    setIsEditMode(false)
                },
            })
        }
        return filterConsultant({
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(TelecomAction.deleteTelecom(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        })
    }, [isEditMode, telecom, storeTelecom, telecoms, telecomTypes, simsLink, materielSettingRules])

    useTitle(() => {
        const { imei, reference = '', serialNumber = '', telecomType, id } = storeTelecom
        const title = imei || serialNumber || reference || id
        const type = (telecomTypes.find(t => t.code === parseInt(telecomType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.telecoms,
                href: 'materiel/telecom',
            },
            {
                title: type,
                href: `materiel/telecom/${params.id}`,
            },
            {
                title,
                href: `materiel/telecom/${params.id}`,
            },
        ])
    }, [storeTelecom, telecomTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_TELECOM)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return undefined
        }
        if (!telecoms.length) {
            dispatch(TelecomAction.fetchTelecoms(true))
        }
        if (!telecomTypes.length) {
            dispatch(TelecomAction.fetchTelecomTypes())
        }
        if (!powerSupplyTypes.length) {
            dispatch(PowerSupplyAction.fetchPowerSupplyTypes())
        }
        if (!sims.length) {
            dispatch(SimAction.fetchSims(true))
        }
        if (!simTypes.length) {
            dispatch(SimAction.fetchSimTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(TelecomAction.resetTelecom())
    }, [])

    useEffect(() => {
        if (params.id !== 'new') {
            fetchTelecom(params.id)
        } else {
            setIsEditMode(true)
        }
    }, [params.id])

    const getMaterielNumber = ({ serialNumber, reference, imei }) => imei || serialNumber || reference || ''

    const getTelecomLabel = t => {
        const number = getMaterielNumber(t)
        if (t.telecomType) {
            const type = telecomTypes.find(type => type.id === t.telecomType)
            if (type) {
                return `${type.label} - ${number}`
            }
        }
        return number
    }

    const getTelecomsFormat = () => telecoms
        .filter(({ isEquipment }) => !isEquipment)
        .map(t => ({
            id: t.id,
            label: getTelecomLabel(t),
        }))

    const onDeleteTelecomSim = ({ idSim, idTelecom }) => {
        dispatch(TelecomAction.deleteTelecomSimLink(idSim, idTelecom)).then(() => dispatch(TelecomAction.fetchSimsLink(params.id)))
    }

    const getSimLabel = ({ serialNumber = '', simType }) => {
        const type = simTypes.find(({ id }) => id === simType)
        return `${type ? `${type.name}:` : ''} ${serialNumber}`
    }

    const formatedLinks = orderBy(simsLink, [({ startDate }) => startDate || 0, ({ endDate }) => endDate || 0], ['desc', 'desc'])
        .map(({ idSim, idTelecom, startDate, endDate, comment }) => {
            const sim = sims.find(({ id }) => idSim === id)
            return {
                sim: sim ? getSimLabel(sim) : '',
                startDate: getDate(startDate),
                endDate: getDate(endDate),
                comment,
                idSim,
                idTelecom,
            }
        })

    const formatedSims = sims.filter(({ isEquipment }) => !isEquipment).map(sim => ({
        code: sim.id,
        name: getSimLabel(sim),
    }))

    const displaySimTable = () => {
        if (!telecom.telecomType) {
            return false
        }
        const telecomType = find(telecomTypes, o => o.id === telecom.telecomType)
        return !!telecomType && [1, 2, 3, 4].includes(telecomType.code)
    }

    const disabled = { active: isEditMode, disabled: !isEditMode }
    const simTableDisplayed = displaySimTable()

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={telecomSituations} />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <TelecomPanel
                                    telecom={telecom}
                                    onChange={changeTelecomValue}
                                    {...disabled}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={storeTelecom.updateDate}
                                updateLogin={storeTelecom.updateLogin}
                                jobExecId={storeTelecom.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={telecom.administrator}
                                        isActive={!storeTelecom.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={telecomSituations} />
                            <SmallPicturePanel pictures={telecomPictures} />
                            <FilePanel files={telecomFiles} />
                        </Stack>
                    </Grid>
                    {
                        !storeTelecom.isEquipment && (
                            <>
                                {simTableDisplayed && !!storeTelecom.id && (
                                    <Grid size={12}>
                                        <SimSubscriptionTable
                                            links={formatedLinks}
                                            editMode={isEditMode}
                                            headers={['sim', 'startDate', 'endDate', 'comment']}
                                            title={i18n.sims}
                                            idKeyToRedirect={'idSim'}
                                            redirectTo={'sim'}
                                            associatedMateriels={formatedSims}
                                            onDelete={onDeleteTelecomSim}
                                            onAdd={({ materielLink, startDate, endDate, comment }) => {
                                                const telecomSim = {
                                                    idTelecom: storeTelecom.id,
                                                    idSim: materielLink,
                                                    startDate,
                                                    endDate,
                                                    comment,
                                                }
                                                dispatch(TelecomAction.createTelecomSim(telecomSim)).then(() =>
                                                    dispatch(TelecomAction.fetchSimsLink(params.id)))
                                            }}
                                        />
                                    </Grid>
                                )}
                                <Grid size={12}>
                                    <SituationPanel
                                        materielSituation={telecomSituations}
                                        active={isEditMode}
                                        idMateriel={storeTelecom.id}
                                        serialNumber={storeTelecom.serialNumber}
                                        purchaseDate={storeTelecom.purchaseDate}
                                        idKey={'idTelecom'}
                                        lastSituations={telecomsLastSituations}
                                        materials={getTelecomsFormat()}
                                        saveFunction={situation => dispatch(TelecomAction.saveTelecomSituation(situation))}
                                        deleteFunction={deleteSituation}
                                    />
                                </Grid>
                            </>
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default TelecomApp